import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Inbox, Mail, Building2, Calendar, RefreshCw, Loader2, Trash2, Download, Copy, Check, Send, UserPlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import EmailHealthCard from "@/components/EmailHealthCard";
import { downloadCsv } from "@/lib/exportCsv";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { InviteBusinessDialog } from "@/components/InviteBusinessDialog";

// CRM-lifecycle labels. `done` is the legacy "Arkivert" bucket kept for
// backwards-compat with older records — it is rendered if it appears in
// data but is intentionally not offered as a new dropdown option.
const STATUS_LABEL = {
  new: "Ny",
  in_review: "Kontaktet",
  demo_scheduled: "Demo avtalt",
  pilot: "Pilotkunde",
  converted: "Konvertert",
  lost: "Tapt",
  done: "Arkivert",
};

const STATUS_BADGE = {
  new:            "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  in_review:      "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",
  demo_scheduled: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800",
  pilot:          "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",
  converted:      "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  lost:           "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800",
  done:           "bg-muted text-muted-foreground border-border",
};

// Options shown in the per-row status dropdown. `converted` is omitted
// here on purpose: it must be set by POST /convert (which also writes
// converted_business_id). Operators who want to manually clear an
// erroneously-converted lead can use the API directly.
const STATUS_OPTIONS = [
  { value: "new",            label: "Ny" },
  { value: "in_review",      label: "Kontaktet" },
  { value: "demo_scheduled", label: "Demo avtalt" },
  { value: "pilot",          label: "Pilotkunde" },
  { value: "lost",           label: "Tapt" },
];

const INTEREST_LABEL = {
  demo: "Demo",
  basic: "Basic",
  pro: "Pro",
  premium: "Premium",
  annet: "Annet spørsmål",
};

// Friendly Norwegian labels for the source tags emitted by each CTA.
// Unknown sources fall back to the raw tag.
const SOURCE_LABEL = {
  "landing-nav": "Book demo (nav)",
  "landing-hero": "Book demo (hero)",
  "landing-final": "Book demo (footer)",
  "landing-plan-demo": "Plan-kort: Demo",
  "landing-plan-basic": "Plan-kort: Basic",
  "landing-plan-pro": "Plan-kort: Pro",
  "settings-billing-basic": "Innstillinger: Oppgrader Basic",
  "settings-billing-pro": "Innstillinger: Oppgrader Pro",
  "settings-billing": "Innstillinger: Pakkeendring",
  "staff-basic-upgrade-pro": "Personal: Basic-grense → oppgrader til Pro",
  "dashboard-plan-badge": "Dashboard: plan-merke",
  "dashboard-demo-banner": "Dashboard: demo-banner",
  "calendar-after-demo-booking": "Etter første demo-booking",
  "calendar-quota-exceeded": "Demo-kvote brukt opp",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" });
}

export default function Forespørsler() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(null); // null = checking, true = allowed, false = forbidden
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null); // the item being replied to
  // "Inviter bedrift"/convert-from-lead dialog. inviteTarget holds the
  // lead being converted; opening the same dialog from anywhere else
  // (currently just the lead row + ReplyDialog) only requires setting
  // this to a lead object. Closing the dialog clears it.
  const [inviteTarget, setInviteTarget] = useState(null);

  // Map an inbound lead → InviteBusinessDialog prefill.
  // * Bedriftsnavn  := lead.company || lead.name fallback
  // * Kontaktperson := lead.name
  // * E-post        := lead.email
  // * Telefon       := optional (we don't capture phone today, so it
  //                    stays blank but the field is editable)
  // The Foresporsler "Inviter bedrift" entry point ALWAYS defaults
  // the plan to Premium per product spec — operators can still change
  // it before submitting.
  const prefillFromLead = (lead) => ({
    business_name: (lead.company || lead.name || "").trim(),
    owner_name: (lead.name || "").trim(),
    owner_email: (lead.email || "").trim(),
    phone: (lead.phone || "").trim?.() || "",
    plan: "premium",
    // free_pilot is the most common stage for converted leads — they
    // typically need a trial period before billing kicks in.
    billing_status: "free_pilot",
    internal_note: lead.message
      ? `Konvertert fra forespørsel.\n\nOpprinnelig melding:\n${lead.message}`
      : "Konvertert fra forespørsel.",
  });

  // Gate on business.is_admin BEFORE rendering the page. Owners of normal
  // businesses (e.g. demo@clientflow.no) get redirected with a clear toast
  // instead of seeing a half-rendered admin layout.
  useEffect(() => {
    let mounted = true;
    api.get("/business")
      .then((r) => {
        if (!mounted) return;
        if (r.data?.is_admin) {
          setAuthorized(true);
        } else {
          setAuthorized(false);
          toast.error("Du har ikke tilgang til Forespørsler.");
          navigate("/dashboard", { replace: true });
        }
      })
      .catch(() => {
        if (!mounted) return;
        setAuthorized(false);
        navigate("/dashboard", { replace: true });
      });
    return () => { mounted = false; };
  }, [navigate]);

  const load = async ({ silent = true } = {}) => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/contact/requests", { params });
      setItems(r.data.items);
      if (!silent) {
        toast.success("Forespørsler oppdatert");
        // Manual "Oppdater" click also refreshes the sidebar badge so a
        // user's mental model — "refresh the page" — updates every count
        // in one click.
        window.dispatchEvent(new Event("cf:sidebar-badges-refresh"));
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Du har ikke tilgang til Forespørsler.");
        navigate("/dashboard", { replace: true });
        return;
      }
      toast.error(silent ? "Klarte ikke å laste forespørsler." : "Kunne ikke oppdatere forespørsler");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authorized === true) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, authorized]);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.put(`/contact/requests/${id}/status`, { status });
      // Optimistic update
      setItems((curr) =>
        (curr || []).map((it) => (it.id === id ? { ...it, status } : it))
      );
      toast.success(`Markert som ${STATUS_LABEL[status]}`);
      // Any status change into or out of "new" affects the sidebar
      // Forespørsler badge; tell AppLayout to refetch immediately.
      window.dispatchEvent(new Event("cf:sidebar-badges-refresh"));
    } catch {
      toast.error("Kunne ikke oppdatere status");
    } finally {
      setUpdating(null);
    }
  };

  const deleteRequest = async (id) => {
    setUpdating(id);
    try {
      await api.delete(`/contact/requests/${id}`);
      setItems((curr) => (curr || []).filter((it) => it.id !== id));
      toast.success("Forespørsel slettet");
      // Deleting a "new" lead decreases the badge; refresh sidebar.
      window.dispatchEvent(new Event("cf:sidebar-badges-refresh"));
    } catch {
      toast.error("Kunne ikke slette forespørsel");
    } finally {
      setUpdating(null);
    }
  };

  // Called by InviteBusinessDialog after a successful POST /convert.
  // The API response includes lead_id + lead_status so we can flip the
  // badge optimistically AND save the new business linkage without a
  // full /contact/requests refetch.
  const onConverted = (resp) => {
    const leadId = resp?.lead_id;
    const businessId = resp?.business_id;
    if (leadId) {
      setItems((curr) =>
        (curr || []).map((it) =>
          it.id === leadId
            ? { ...it, status: "converted", converted_business_id: businessId }
            : it
        )
      );
    }
    setInviteTarget(null);
    // Also clear the reply dialog if it was the source of the click.
    setReplyOpen(false);
    setReplyTarget(null);
    // Convert flips a lead out of "new" if it was still pending; refresh
    // sidebar badge immediately.
    window.dispatchEvent(new Event("cf:sidebar-badges-refresh"));
  };

  const counts = (items || []).reduce(
    (acc, it) => {
      acc[it.status] = (acc[it.status] || 0) + 1;
      return acc;
    },
    { new: 0, in_review: 0, demo_scheduled: 0, pilot: 0, converted: 0, lost: 0, done: 0 }
  );

  return (
    <div className="space-y-6" data-testid="leads-page">
      {authorized !== true ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="leads-checking">
          <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
          Sjekker tilgang...
        </div>
      ) : (
        <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">Plattform-admin</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Forespørsler</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Innkommende henvendelser fra clientflow.no — kun synlig for ClientFlow-administrator.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => load({ silent: false })}
          disabled={loading}
          data-testid="leads-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
          Oppdater
        </Button>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => downloadCsv("/export/leads.csv", "forespørsler.csv", "forespørsler")}
          data-testid="export-leads-btn"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Eksporter forespørsler
        </Button>
      </div>

      <div className="flex">
        <Button
          className="rounded-xl"
          onClick={() => navigate("/admin/bedrifter")}
          data-testid="leads-go-to-bedrifter"
        >
          <Building2 className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Administrer bedrifter
        </Button>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-3 gap-3">
        <StatChip label="Ny" value={counts.new} testid="count-new" />
        <StatChip label="Kontaktet" value={counts.in_review} testid="count-in-review" />
        <StatChip label="Arkivert" value={counts.done} testid="count-done" />
      </div>

      {/* Super Admin-only e-post-diagnostikk. Component self-hides for
          non-admin users (the underlying endpoint 403s). */}
      <EmailHealthCard />

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 rounded-xl" data-testid="leads-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="new">Ny</SelectItem>
            <SelectItem value="in_review">Kontaktet</SelectItem>
            <SelectItem value="demo_scheduled">Demo avtalt</SelectItem>
            <SelectItem value="pilot">Pilotkunde</SelectItem>
            <SelectItem value="converted">Konvertert</SelectItem>
            <SelectItem value="lost">Tapt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading && !items ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
          Laster forespørsler...
        </div>
      ) : !items || items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="leads-empty">
          <Inbox className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" strokeWidth={1.5} />
          <p className="text-foreground font-medium">Ingen forespørsler ennå.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Skjemaer på clientflow.no havner her når noen sender dem inn.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div
              key={it.id}
              className="rounded-2xl border border-border bg-card p-5 sm:p-6 anim-in"
              data-testid={`lead-${it.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-base">{it.name}</h3>
                    <Badge
                      variant="outline"
                      className={`rounded-full text-[10px] uppercase tracking-wider ${STATUS_BADGE[it.status] || ""}`}
                      data-testid={`lead-status-${it.id}`}
                    >
                      {STATUS_LABEL[it.status] || it.status}
                    </Badge>
                    {it.interest && (
                      <Badge
                        variant="outline"
                        className="rounded-full text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800"
                        data-testid={`lead-interest-${it.id}`}
                      >
                        {INTEREST_LABEL[it.interest] || it.interest}
                      </Badge>
                    )}
                    {it.source && (
                      <Badge variant="outline" className="rounded-full text-[10px] bg-accent text-accent-foreground border-border">
                        {SOURCE_LABEL[it.source] || it.source}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
                    <a
                      href={`mailto:${it.email}`}
                      className="inline-flex items-center gap-1.5 text-foreground hover:underline underline-offset-4"
                      data-testid={`lead-email-${it.id}`}
                    >
                      <Mail className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
                      {it.email}
                    </a>
                    {it.company && (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Building2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {it.company}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5" strokeWidth={1.75} />
                      {fmtDate(it.created_at)}
                    </span>
                  </div>
                  {it.message && (
                    <p className="mt-3 text-sm text-foreground/80 whitespace-pre-line border-l-2 border-border pl-3">
                      {it.message}
                    </p>
                  )}
                  {it.status === "converted" && it.converted_business_id && (
                    <Link
                      to="/admin/bedrifter"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                      data-testid={`lead-converted-link-${it.id}`}
                    >
                      Se bedriften <ArrowRight className="w-3 h-3" strokeWidth={2.25} />
                    </Link>
                  )}
                </div>
                <div className="flex sm:flex-col gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg w-full"
                    data-testid={`lead-reply-${it.id}`}
                    onClick={() => { setReplyTarget(it); setReplyOpen(true); }}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
                    Svar
                  </Button>
                  {it.status !== "converted" && (
                    <Button
                      size="sm"
                      className="rounded-lg w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid={`lead-invite-${it.id}`}
                      onClick={() => setInviteTarget(it)}
                    >
                      <UserPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                      Inviter bedrift
                    </Button>
                  )}
                  <Select
                    value={it.status}
                    onValueChange={(v) => setStatus(it.id, v)}
                    disabled={updating === it.id}
                  >
                    <SelectTrigger className="rounded-lg" data-testid={`lead-status-select-${it.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                      {/* Show the current value as a non-default option so
                          previously-set `converted` or legacy `done`
                          rows still display correctly in the trigger. */}
                      {!STATUS_OPTIONS.some((o) => o.value === it.status) && (
                        <SelectItem value={it.status}>{STATUS_LABEL[it.status] || it.status}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={updating === it.id}
                        data-testid={`lead-delete-${it.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
                        Slett
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`lead-delete-confirm-${it.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Slett forespørsel?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Vil du slette henvendelsen fra <strong>{it.name}</strong> permanent?
                          Dette kan ikke angres. Bruk <strong>Arkivert</strong> i stedet hvis du
                          bare vil skjule den.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`lead-delete-cancel-${it.id}`}>Avbryt</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => deleteRequest(it.id)}
                          data-testid={`lead-delete-confirm-btn-${it.id}`}
                        >
                          Slett
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}
      <ReplyDialog
        open={replyOpen}
        onOpenChange={(v) => { setReplyOpen(v); if (!v) setReplyTarget(null); }}
        target={replyTarget}
        onMarkContacted={(id) => setStatus(id, "in_review")}
        onNoteSaved={(id, note) => {
          setItems((curr) =>
            (curr || []).map((it) => (it.id === id ? { ...it, internal_note: note } : it))
          );
        }}
        onConvert={(lead) => { setInviteTarget(lead); }}
      />
      <InviteBusinessDialog
        open={!!inviteTarget}
        onOpenChange={(v) => { if (!v) setInviteTarget(null); }}
        leadId={inviteTarget?.id}
        defaultPlan="premium"
        initial={inviteTarget ? prefillFromLead(inviteTarget) : null}
        onCreated={onConverted}
      />
    </div>
  );
}

function StatChip({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

// Pre-filled reply text for the mailto fallback. Kept short and Norwegian.
const REPLY_SUBJECT = "Svar fra ClientFlow";
const REPLY_BODY = "Hei, takk for henvendelsen din.";

function ReplyDialog({ open, onOpenChange, target, onMarkContacted, onNoteSaved, onConvert }) {
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Reset the "Kopiert" pill and load the latest internal_note every time
  // the dialog re-opens.
  useEffect(() => {
    if (open) {
      setCopied(false);
      setNote(target?.internal_note || "");
    }
  }, [open, target?.id, target?.internal_note]);

  if (!target) return null;

  const email = target.email || "";
  const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(REPLY_SUBJECT)}&body=${encodeURIComponent(REPLY_BODY)}`;
  const alreadyContacted = target.status === "in_review" || target.status === "done";

  const copyEmail = async () => {
    // `navigator.clipboard.writeText` is the modern path but it can reject
    // with a NotAllowedError when the document isn't focused, when the page
    // is served over http, or when the user has explicitly blocked
    // clipboard permission. In any of those cases we silently fall back to
    // the legacy textarea + execCommand trick, which works inside a
    // synchronous user-click event handler in every browser.
    const legacyCopy = () => {
      const ta = document.createElement("textarea");
      ta.value = email;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    };
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
        ok = true;
      } else {
        ok = legacyCopy();
      }
    } catch {
      ok = legacyCopy();
    }
    if (ok) {
      setCopied(true);
      toast.success("E-postadresse kopiert");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Klarte ikke å kopiere");
    }
  };

  const markContacted = () => {
    onMarkContacted?.(target.id);
    onOpenChange(false);
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/contact/requests/${target.id}/note`, { internal_note: note });
      onNoteSaved?.(target.id, note);
      toast.success("Internt notat lagret");
    } catch {
      toast.error("Kunne ikke lagre notat");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="reply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Svar på henvendelse</DialogTitle>
          <DialogDescription>
            Kopier e-postadressen og svar fra ditt vanlige e-postprogram, eller
            åpne mailto direkte.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm space-y-1">
          {target.name && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Navn</span>
              <span className="font-medium truncate" data-testid="reply-name">{target.name}</span>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">E-post</span>
            <span className="font-medium truncate" data-testid="reply-email">{email}</span>
          </div>
          {target.company && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Bedrift</span>
              <span className="font-medium truncate" data-testid="reply-company">{target.company}</span>
            </div>
          )}
          {target.interest && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Pakke</span>
              <span className="font-medium">{INTEREST_LABEL[target.interest] || target.interest}</span>
            </div>
          )}
        </div>

        {target.message && (
          <div className="mt-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              Melding
            </p>
            <p className="text-sm text-foreground/85 whitespace-pre-line border-l-2 border-border pl-3" data-testid="reply-message">
              {target.message}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl flex-1 min-w-[180px]"
            onClick={copyEmail}
            data-testid="reply-copy-email"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1.5" strokeWidth={2} />
                Kopiert
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                Kopier e-postadresse
              </>
            )}
          </Button>
          <a
            href={mailto}
            className="flex-1 min-w-[140px]"
            data-testid="reply-mailto-link"
          >
            <Button type="button" variant="outline" className="rounded-xl w-full">
              <Send className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Åpne e-post
            </Button>
          </a>
        </div>

        <div className="pt-3 border-t border-border space-y-2">
          <Label htmlFor="reply-internal-note" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Internt notat
          </Label>
          <Textarea
            id="reply-internal-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="F.eks. Ringte 13:42 – vurderer Pro."
            className="rounded-xl"
            data-testid="reply-internal-note"
          />
          <p className="text-[11px] text-muted-foreground">
            Kun synlig for ClientFlow-administrator. Sendes aldri til kunden.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={saveNote}
            disabled={savingNote}
            data-testid="reply-save-note"
          >
            {savingNote ? "Lagrer..." : "Lagre notat"}
          </Button>
        </div>

        <div className="pt-2 space-y-2">
          <Button
            type="button"
            className="rounded-xl w-full"
            onClick={markContacted}
            disabled={alreadyContacted}
            data-testid="reply-mark-contacted"
          >
            <Check className="w-4 h-4 mr-1.5" strokeWidth={2} />
            {alreadyContacted ? "Allerede markert som kontaktet" : "Marker som kontaktet"}
          </Button>
          {/* One-click conversion from inside the detail view. Hidden
              when the lead is already converted — the row's link to
              the business is enough in that case. */}
          {target.status !== "converted" && (
            <Button
              type="button"
              className="rounded-xl w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              onClick={() => onConvert?.(target)}
              data-testid="reply-invite-business"
            >
              <UserPlus className="w-4 h-4 mr-1.5" strokeWidth={2} />
              Inviter bedrift
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
